import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiChevronLeft, FiChevronRight, FiCalendar } from 'react-icons/fi';
import { formatDateToISO } from '../../utils/formatters';
import { getTasks, getTaskPriorityColor } from '../../api/tasks';
import TaskPriorityIndicator from '../../components/tasks/TaskPriorityIndicator';
import TaskStatusBadge from '../../components/tasks/TaskStatusBadge';
import LoadingSpinner from '../../components/common/LoadingSpinner';

const TaskCalendarPage = () => {
  const navigate = useNavigate();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedDay, setSelectedDay] = useState(null);

  useEffect(() => {
    fetchTasks();
  }, [currentDate]);

  const fetchTasks = async () => {
    setLoading(true);
    setError(null);
    try {
      // Get tasks for the current month
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth();
      const firstDay = new Date(year, month, 1);
      const lastDay = new Date(year, month + 1, 0);

      const response = await getTasks({
        due_date_from: formatDateToISO(firstDay),
        due_date_to: formatDateToISO(lastDay),
        per_page: 1000 // Get all tasks for the month
      });

      if (response.success) {
        setTasks(response.data || []);
      } else {
        throw new Error(response.message || 'Failed to fetch tasks');
      }
    } catch (err) {
      console.error('Error fetching tasks:', err);
      setError(err.response?.data?.message || err.message || 'Failed to load tasks');
    } finally {
      setLoading(false);
    }
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay(); // 0 = Sunday

    const days = [];

    // Add empty slots for days before the first of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }

    // Add all days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }

    return days;
  };

  const getTasksForDay = (date) => {
    if (!date) return [];
    const dateStr = formatDateToISO(date);
    return tasks.filter((task) => task.due_date === dateStr);
  };

  const goToPreviousMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
    setSelectedDay(null);
  };

  const goToNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
    setSelectedDay(null);
  };

  const goToToday = () => {
    setCurrentDate(new Date());
    setSelectedDay(null);
  };

  const isToday = (date) => {
    if (!date) return false;
    const today = new Date();
    return (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    );
  };

  const isSameDay = (date1, date2) => {
    if (!date1 || !date2) return false;
    return (
      date1.getDate() === date2.getDate() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getFullYear() === date2.getFullYear()
    );
  };

  const handleDayClick = (date) => {
    if (!date) return;
    setSelectedDay(isSameDay(selectedDay, date) ? null : date);
  };

  const handleTaskClick = (taskId) => {
    navigate(`/tasks/${taskId}`);
  };

  const monthYear = currentDate.toLocaleString('en-US', { month: 'long', year: 'numeric' });
  const days = getDaysInMonth(currentDate);
  const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const selectedDayTasks = selectedDay ? getTasksForDay(selectedDay) : [];

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-primary-black">Task Calendar</h1>
            <p className="mt-1 text-sm text-gray-500">View and manage tasks by their due dates</p>
          </div>
          <button
            onClick={goToToday}
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent-gold"
          >
            <FiCalendar className="mr-2 h-4 w-4" />
            Today
          </button>
        </div>
      </div>

      {/* Calendar Navigation */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <button
            onClick={goToPreviousMonth}
            className="p-2 rounded-md hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-accent-gold"
          >
            <FiChevronLeft className="h-5 w-5 text-gray-600" />
          </button>
          <h2 className="text-lg font-semibold text-primary-black">{monthYear}</h2>
          <button
            onClick={goToNextMonth}
            className="p-2 rounded-md hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-accent-gold"
          >
            <FiChevronRight className="h-5 w-5 text-gray-600" />
          </button>
        </div>

        {/* Calendar Grid */}
        <div className="p-6">
          {loading ? (
            <LoadingSpinner message="Loading task calendar..." className="py-12" />
          ) : error ? (
            <div className="bg-red-50 border border-red-200 rounded-md p-4">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          ) : (
            <div className="grid grid-cols-7 gap-2">
              {/* Week day headers */}
              {weekDays.map((day) => (
                <div key={day} className="text-center text-xs font-semibold text-gray-600 py-2">
                  {day}
                </div>
              ))}

              {/* Calendar days */}
              {days.map((date, index) => {
                const dayTasks = getTasksForDay(date);
                const isSelected = isSameDay(date, selectedDay);
                const isTodayDate = isToday(date);

                return (
                  <div
                    key={index}
                    onClick={() => handleDayClick(date)}
                    className={`min-h-[100px] p-2 border rounded-md cursor-pointer transition-all ${
                      !date
                        ? 'bg-gray-50 cursor-default'
                        : isSelected
                          ? 'bg-yellow-50 border-accent-gold ring-2 ring-accent-gold'
                          : isTodayDate
                            ? 'bg-blue-50 border-blue-300'
                            : 'bg-white border-gray-200 hover:border-accent-gold hover:bg-gray-50'
                    }`}
                  >
                    {date && (
                      <>
                        <div
                          className={`text-sm font-medium mb-1 ${
                            isTodayDate
                              ? 'text-blue-600'
                              : isSelected
                                ? 'text-accent-goldDark'
                                : 'text-gray-700'
                          }`}
                        >
                          {date.getDate()}
                        </div>
                        <div className="space-y-1">
                          {dayTasks.slice(0, 3).map((task) => (
                            <div
                              key={task.id}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleTaskClick(task.id);
                              }}
                              className="text-xs p-1 rounded truncate cursor-pointer hover:opacity-80 transition-opacity"
                              style={{
                                backgroundColor: getTaskPriorityColor(task.priority) + '20',
                                borderLeft: `3px solid ${getTaskPriorityColor(task.priority)}`
                              }}
                              title={task.title}
                            >
                              {task.title}
                            </div>
                          ))}
                          {dayTasks.length > 3 && (
                            <div className="text-xs text-gray-500 pl-1">
                              +{dayTasks.length - 3} more
                            </div>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Selected Day Tasks Panel */}
      {selectedDay && selectedDayTasks.length > 0 && (
        <div className="mt-6 bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-primary-black mb-4">
            Tasks for{' '}
            {selectedDay.toLocaleDateString('en-US', {
              weekday: 'long',
              month: 'long',
              day: 'numeric',
              year: 'numeric'
            })}
          </h3>
          <div className="space-y-3">
            {selectedDayTasks.map((task) => (
              <div
                key={task.id}
                onClick={() => handleTaskClick(task.id)}
                className="p-4 border border-gray-200 rounded-md hover:border-accent-gold hover:shadow-sm transition-all cursor-pointer"
                style={{
                  borderLeftWidth: '4px',
                  borderLeftColor: getTaskPriorityColor(task.priority)
                }}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="text-sm font-medium text-primary-black">{task.title}</h4>
                      <TaskPriorityIndicator priority={task.priority} size="sm" />
                    </div>
                    <p className="text-xs text-gray-500 mb-2">{task.task_number}</p>
                    {task.description && (
                      <p className="text-sm text-gray-600 line-clamp-2">{task.description}</p>
                    )}
                  </div>
                  <div className="ml-4">
                    <TaskStatusBadge status={task.status} size="sm" />
                  </div>
                </div>
                <div className="mt-3 flex items-center gap-4 text-xs text-gray-500">
                  {task.assigned_to_name && (
                    <div>
                      <span className="font-medium">Assigned to:</span> {task.assigned_to_name}
                    </div>
                  )}
                  {task.due_time && (
                    <div>
                      <span className="font-medium">Time:</span> {task.due_time}
                    </div>
                  )}
                  {task.category && (
                    <div>
                      <span className="font-medium">Category:</span> {task.category}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Legend */}
      <div className="mt-6 bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-sm font-semibold text-primary-black mb-3">Priority Legend</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="flex items-center gap-2">
            <TaskPriorityIndicator priority="low" size="sm" />
            <span className="text-sm text-gray-600">Low Priority</span>
          </div>
          <div className="flex items-center gap-2">
            <TaskPriorityIndicator priority="medium" size="sm" />
            <span className="text-sm text-gray-600">Medium Priority</span>
          </div>
          <div className="flex items-center gap-2">
            <TaskPriorityIndicator priority="high" size="sm" />
            <span className="text-sm text-gray-600">High Priority</span>
          </div>
          <div className="flex items-center gap-2">
            <TaskPriorityIndicator priority="urgent" size="sm" />
            <span className="text-sm text-gray-600">Urgent</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TaskCalendarPage;
